import { useState, useEffect } from 'react';

import { supabase } from '../../../services/supabase/client';

const BUCKET_NAME = 'collaborations';

/**
 * 获取 Collaboration 数据的 Hook
 * 用于收藏卡片显示正确的 collaboration 图片
 * 参考 useWorkData 的实现，直接从 Supabase 获取数据
 */
export const useCollaborationData = () => {
  const [collaborationsData, setCollaborationsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 加载所有 collaboration 数据
  useEffect(() => {
    const loadCollaborationsData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(
          '[useCollaborationData] Loading collaborations from Supabase...'
        );

        // 从 Supabase 获取所有 collaboration 数据
        const { data, error: queryError } = await supabase
          .from('collaborations')
          .select('*')
          .order('created_at', { ascending: false });

        if (queryError) {
          console.error(
            '[useCollaborationData] Supabase query error:',
            queryError
          );
          setError(queryError.message);
          return;
        }

        if (!data || data.length === 0) {
          console.log('[useCollaborationData] No collaborations found');
          setCollaborationsData({});
          return;
        }

        console.log(
          `[useCollaborationData] Found ${data.length} collaborations`
        );

        // 将 collaboration 数据转换为以ID为键的对象
        const collaborationsById = {};

        // 并行处理所有图片URL转换和author信息
        const imagePromises = data.map(async item => {
          if (item && item.id) {
            let imageUrl = '';

            // 优先使用 poster_preview，如果没有则使用 hero_image
            const imagePath = item.poster_preview || item.hero_image;

            if (imagePath) {
              try {
                // 如果是存储路径（不是完整URL），转换为公开URL
                if (
                  imagePath &&
                  !imagePath.startsWith('http') &&
                  !imagePath.startsWith('blob:') &&
                  !imagePath.startsWith('data:')
                ) {
                  const { data: urlData } = supabase.storage
                    .from(BUCKET_NAME)
                    .getPublicUrl(imagePath);
                  imageUrl = urlData.publicUrl;
                  console.log(
                    `[useCollaborationData] Converted storage path to URL: ${imagePath} -> ${imageUrl.substring(
                      0,
                      50
                    )}...`
                  );
                } else {
                  // 已经是完整URL，直接使用
                  imageUrl = imagePath;
                }
              } catch (error) {
                console.warn(
                  '[useCollaborationData] Failed to get image URL for:',
                  imagePath,
                  error
                );
              }
            }

            // 获取 author 信息（与 convertFromDatabaseFormat 保持一致）
            let author = {
              id: item.creator_id,
              name: 'Unknown',
              avatar: null,
              role: 'Initiator',
            };

            if (item.creator_id) {
              try {
                const { data: profile, error: profileError } = await supabase
                  .from('profiles')
                  .select('full_name, avatar_url')
                  .eq('id', item.creator_id)
                  .single();

                if (!profileError && profile) {
                  const displayName =
                    profile.full_name ||
                    `User_${item.creator_id.substring(0, 8)}` ||
                    'Unknown';

                  author = {
                    id: item.creator_id,
                    name: displayName,
                    avatar: profile.avatar_url || null,
                    role: 'Initiator',
                  };
                }
              } catch (error) {
                console.warn(
                  '[useCollaborationData] Failed to get author profile:',
                  error
                );
              }
            }

            return {
              id: item.id,
              title: item.title || 'Untitled',
              description: item.description || '',
              projectVision: item.project_vision || '',
              whyThisMatters: item.why_this_matters || '',
              teamSize: item.team_size || '',
              duration: item.duration || '',
              meetingSchedule: item.meeting_schedule || '',
              applicationDeadline: item.application_deadline || '',
              projectType: item.project_type || '',
              contactInfo: item.contact_info || {},
              heroImage: imageUrl,
              posterPreview: imageUrl,
              image: imageUrl, // 添加 image 字段（作为回退）
              status: item.status || 'active',
              likes: item.likes || 0,
              views: item.views || 0,
              createdAt: item.created_at || new Date().toISOString(),
              updatedAt: item.updated_at || new Date().toISOString(),
              creatorId: item.creator_id,
              // 添加 author 对象（与 formatAPIDataForList 格式一致）
              author: {
                id: author.id,
                name: author.name,
                avatar: author.avatar,
                role: author.role,
              },
              // 添加 roles 字段（如果有）
              roles: item.collaboration_roles
                ? item.collaboration_roles.map(role => ({
                    id: role.id,
                    title: role.title,
                    description: role.description,
                    requiredSkills: role.required_skills || [],
                    status: role.status,
                  }))
                : [],
            };
          }
          return null;
        });

        const processedItems = await Promise.all(imagePromises);

        processedItems.forEach(item => {
          if (item) {
            collaborationsById[item.id] = item;
          }
        });

        setCollaborationsData(collaborationsById);
        console.log(
          '[useCollaborationData] Loaded collaborations data:',
          Object.keys(collaborationsById).length,
          'items'
        );
      } catch (error) {
        console.error(
          '[useCollaborationData] Error loading collaborations:',
          error
        );
        setError(error.message || 'Failed to load collaborations data');
      } finally {
        setLoading(false);
      }
    };

    loadCollaborationsData();
  }, []);

  // 根据itemId获取 collaboration 数据
  const getCollaborationById = itemId => {
    if (!itemId) return null;

    // 首先尝试从真实数据中获取
    const realCollaboration = collaborationsData[itemId];
    if (realCollaboration) {
      console.log(
        '[useCollaborationData] Found real collaboration data for:',
        itemId,
        realCollaboration
      );
      return realCollaboration;
    }

    // 如果真实数据加载完成且找不到 collaboration，返回 null（表示不存在）
    if (!loading && Object.keys(collaborationsData).length > 0) {
      console.warn(
        '[useCollaborationData] Collaboration not found in real data:',
        itemId
      );
      return null;
    }

    // 如果数据还在加载中，返回 null（等待加载完成）
    return null;
  };

  return {
    collaborationsData,
    loading,
    error,
    getCollaborationById,
  };
};
